import type { ChartConfiguration } from 'chart.js';
import { KpiCard, MonthlyAnalyticsPoint } from './admin-dashboard.models';

const BRAND = {
  midnight: '#0B3558',
  action:   '#006BFF',
  slate:    '#476788',
  mist:     '#F8F9FB',
  border:   '#E7EDF6',
};

export function sparklineConfig(card: KpiCard): ChartConfiguration<'line'> {
  const stroke = card.deltaPositive ? '#059669' : '#DC2626';
  return {
    type: 'line',
    data: {
      labels: card.sparkline.map((_, i) => `${i}`),
      datasets: [{
        data: card.sparkline,
        borderColor: stroke,
        backgroundColor: `${stroke}22`,
        borderWidth: 2,
        fill: true,
        tension: 0.35,
        pointRadius: 0,
        pointHitRadius: 0,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 400 },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      scales: {
        x: { display: false },
        y: { display: false },
      },
    },
  };
}

export function revenueBookingsConfig(series: MonthlyAnalyticsPoint[]): ChartConfiguration {
  return {
    type: 'bar',
    data: {
      labels: series.map(p => p.month),
      datasets: [
        {
          type: 'bar',
          label: 'Reservas',
          data: series.map(p => p.bookings),
          backgroundColor: `${BRAND.midnight}CC`,
          borderRadius: 6,
          yAxisID: 'y',
          order: 2,
        },
        {
          type: 'line',
          label: 'Ingresos (€)',
          data: series.map(p => p.revenueEur),
          borderColor: BRAND.action,
          backgroundColor: BRAND.action,
          borderWidth: 3,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#fff',
          pointBorderColor: BRAND.action,
          pointBorderWidth: 2,
          tension: 0.3,
          yAxisID: 'y1',
          order: 1,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      animation: { duration: 500 },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: {
            color: BRAND.midnight,
            font: { family: 'Montserrat, system-ui, sans-serif', size: 11, weight: 'bold' },
            usePointStyle: true,
            boxWidth: 8,
          },
        },
        tooltip: {
          backgroundColor: BRAND.midnight,
          titleColor: '#fff',
          bodyColor: BRAND.mist,
          borderColor: BRAND.action,
          borderWidth: 1,
          padding: 12,
          titleFont: { family: 'Montserrat, system-ui, sans-serif', size: 12, weight: 'bold' },
          bodyFont: { family: 'Montserrat, system-ui, sans-serif', size: 11 },
          callbacks: {
            title: items => `TimeFlexRent · ${items[0]?.label ?? ''}`,
            label: ctx => {
              const v = ctx.parsed.y;
              if (v == null) return '';
              return ctx.dataset.label === 'Ingresos (€)'
                ? ` ${ctx.dataset.label}: €${v.toLocaleString('es-ES')}`
                : ` ${ctx.dataset.label}: ${v}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: BRAND.slate,
            font: { family: 'Montserrat, system-ui, sans-serif', size: 11 },
          },
        },
        y: {
          position: 'left',
          grid: { color: BRAND.border },
          ticks: {
            color: BRAND.slate,
            font: { family: 'Montserrat, system-ui, sans-serif', size: 10 },
          },
          title: {
            display: true,
            text: 'Reservas',
            color: BRAND.midnight,
            font: { size: 10, weight: 'bold' },
          },
        },
        y1: {
          position: 'right',
          grid: { drawOnChartArea: false },
          ticks: {
            color: BRAND.action,
            callback: v => `€${Number(v) / 1000}k`,
            font: { family: 'Montserrat, system-ui, sans-serif', size: 10 },
          },
          title: {
            display: true,
            text: 'Ingresos',
            color: BRAND.action,
            font: { size: 10, weight: 'bold' },
          },
        },
      },
    },
  };
}

/** Minimal handle stored for teardown — avoids Chart.js generic variance issues. */
export type ChartInstance = {
  destroy(): void;
  resize(): void;
};
